import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { useTenantContext } from '@/lib/hooks/use-tenant-context';
import { useAuth } from '@/features/auth/auth-provider';
import { useGetApiAgents } from '@/lib/api/generated/agents/agents';
import { postApiLightweightAutomations } from '@/lib/api/generated/automations/automations';
import { useTenantDirectoryUsersQuery } from '@/lib/query/tenant-app-users';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/empty-state';
import { toast } from 'sonner';
import { ROUTES } from '@/app/routes';
import { useDocumentTitle } from '@/lib/hooks/use-document-title';
import { queryKeys } from '@/lib/query/keys';
import { toastMutationError } from '@/lib/utils/formatting';
import { AutomationForm } from './automation-form';
import type { LightweightAutomationRecord } from './automation-domain';
import {
  AUTOMATION_TEMPLATES,
  buildTemplateValues,
  type AutomationTemplateKey,
} from './templates';

export function CreateAutomationPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const { user, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const tenantScopedQueryOptions = { query: { enabled: Boolean(activeTenantId) } };
  const { data: agents, isLoading } = useGetApiAgents(tenantScopedQueryOptions);
  const directoryUsersQuery = useTenantDirectoryUsersQuery(activeTenantId);
  const requestedTemplate = parseAutomationTemplateKey(searchParams.get('template'));
  const [template, setTemplate] = useState<AutomationTemplateKey>(requestedTemplate);
  useDocumentTitle('New Automation');

  useEffect(() => {
    setTemplate(requestedTemplate);
  }, [requestedTemplate]);

  const initialValues = useMemo(() => buildTemplateValues(activeTenantId, template), [activeTenantId, template]);
  const selectedTemplate = useMemo(
    () => AUTOMATION_TEMPLATES.find((entry) => entry.key === template) ?? AUTOMATION_TEMPLATES[0],
    [template],
  );
  const currentDirectoryUser = useMemo(
    () => directoryUsersQuery.data?.items.find((entry) => entry.id === user?.actorId) ?? null,
    [directoryUsersQuery.data?.items, user?.actorId],
  );
  const canAuthorShared = Boolean(
    isPlatformAdmin || currentDirectoryUser?.roleKeys.includes('agentiq_admin'),
  );
  const mutation = useMutation({
    mutationFn: (record: LightweightAutomationRecord) =>
      postApiLightweightAutomations(
        { record: record as Record<string, unknown> },
        { headers: { "x-tenant-id": record.tenantId } },
      ),
    onSuccess: async (_record, input) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/lightweight-automations', { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: [`/api/lightweight-automations/${input.automationId}`, { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });

  const handleSubmit = async (record: LightweightAutomationRecord) => {
    try {
      await mutation.mutateAsync(record);
      toast.success('Automation created');
      navigate(ROUTES.tenantAutomations(activeTenantId));
    } catch (err) {
      toast.error(toastMutationError(err, 'Failed to create automation'));
    }
  };

  if (isLoading) {
    return <EmptyState title="Loading agents" description="Preparing the automation editor." />;
  }

  if (!agents || agents.length === 0) {
    return <EmptyState title="No agents available" description="Create at least one tenant agent before creating an automation." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="gap-2">
          <CardTitle>New Automation</CardTitle>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Pick a starting point, then define the task in instructions. Delivery and schedule stay structured.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {AUTOMATION_TEMPLATES.map((entry) => (
              <Button
                key={entry.key}
                type="button"
                variant={template === entry.key ? 'outline' : 'ghost'}
                size="sm"
                onClick={() => setTemplate(entry.key)}
              >
                <Badge variant="outline">{entry.label}</Badge>
              </Button>
            ))}
          </div>
          <div
            className="rounded-sm border border-border p-3 text-xs"
            style={{ background: 'var(--bg-elevated)', color: 'var(--muted-foreground)' }}
          >
            <span className="font-medium" style={{ color: 'var(--text-strong)' }}>
              Starting point: {selectedTemplate.label}
            </span>
            <br />
            {selectedTemplate.description}
          </div>
        </CardContent>
      </Card>

      <AutomationForm
        tenantId={activeTenantId}
        title="Automation details"
        submitLabel="Create automation"
        agents={agents}
        tenantUsers={directoryUsersQuery.data?.items ?? []}
        currentUserId={user?.actorId ?? ''}
        canAuthorShared={canAuthorShared}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={() => navigate(ROUTES.tenantAutomations(activeTenantId))}
      />
    </div>
  );
}

function parseAutomationTemplateKey(value: string | null): AutomationTemplateKey {
  return (
    AUTOMATION_TEMPLATES.find((entry) => entry.key === value)?.key ?? 'blank'
  );
}
